import { toSvg } from "html-to-image";
import { omit } from "lodash";
import { SourceSourceFields, } from "pages/RS-Workspace/components/Results/components/Elements/components/ProcessDataViewerElement/types";
import { SourceFields, } from "pages/RS-Workspace/components/Results/components/Elements/components/SpaceViewerElement/types";
import { INCOMPLETE_ENTITIES_MESSAGES } from "shared/labels";
import { findDictionaryItemByValue, getDefaultUnitsPrefix, N_A, recipeParameterUnitsType, timeUnits, } from "shared/rsDictionary";
import { SpaceviewerAxisType, TimeUnits, } from "shared/types";
import { SOURCE_LINE_COLORS } from "shared/colors";
import { compareResultItemIds, getArrayMinMax } from "utils/data";
import { isNotEmpty } from "utils/validate";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { CustomVariableRelativeValueVariableType, EventValueType, } from "store/recipe/recipe.types";
import { flatSteps, getAlternativeStepFlowsList, getRecipeTimestamps, getUnitsCategory, } from "store/recipe/recipe.utils";
import { checkInsightHasInvalidScore, checkOptimizationErrored, getCutEventLabel, getSelectedTimestamp, } from "./insights.utils";
import { calculationFinishedStates, CalculationStatusSource, CellState, InsightInputType, ProcessDataEntityTarget, ProcessVariableType, ResultElementGraphViewType, ResultElementParameterType, ResultElementSourceAppearance, ResultElementSourceType, ResultSetElementType, ScenarioNodeType, SimulationRunState, TableCellResultType, TableElementSourceType, } from "../rs-workspace.types";
import { getInsightSourceName, getRecipesCustomVariables, getSimulationSourceName } from "./scenario.utils";
import { getAlternativeStepCompleteness, getCellModelParameterCompleteness, getRecipeParameterCompleteness, getTableColumnCompletenessMessage, isLineGraphSourceCompleted, isOptimizationCompleted, isParameterDimensionCompleted, isProcessDataViewerSourceCompleted, isProcessDataViewerSourceSourceCompleted, isSimulationCompleted, isSimulationVariantCompleted, isTableElementSourceCompleted, } from "./completeness.utils";
import { getCombinationParameterPreview, getEmptySpaceviewerResultSource, getUsedTargetsFromSource, } from "./common.utils";
import { OUTPUT_FINAL_ID } from "../selectors/rs-workspace.selectors";
import { convertToCSV, getTextContentFromHtml } from "utils/string";
import { sleep } from "utils/network";
import { getColumnLabels, getRowName, } from "pages/RS-Workspace/components/Results/components/Elements/components/TableElement/utils";
import { getTotalRuns } from "./calculations.utils";
import { formatCellModelParameterValue } from "store/cell-model/cell-model.utils";
import * as elementItemStyles from "pages/RS-Workspace/components/Results/components/Elements/components/ElementItem/styles.module.scss";
import * as tableElementStyles from "pages/RS-Workspace/components/Results/components/Elements/components/TableElement/styles.module.scss";
import * as tableStyles from "pages/RS-Workspace/components/Results/components/Elements/components/TableElement/components/Table/styles.module.scss";
import * as lineGraphElementStyles from "pages/RS-Workspace/components/Results/components/Elements/components/LineGraphElement/styles.module.scss";
import * as processDataViewerElementStyles from "pages/RS-Workspace/components/Results/components/Elements/components/ProcessDataViewerElement/styles.module.scss";
import * as spaceViewerStyles from "rs-components/Spaceviewer/styles.module.scss";
import * as spaceViewerElementStyles from "pages/RS-Workspace/components/Results/components/Elements/components/SpaceViewerElement/styles.module.scss";
import { getDefaultGraphData, getGraphDataWithSpread, getProcessDataViewerTraces } from "./graph.utils";
import { getProcessVariablesOptions } from "store/global/global.utils";
import { getErrorsTrace } from "shared/plotly/helpers";
import { getTimeUnit } from "utils/dateTime";
export const MAX_RESULT_SETS = 6;
export const MAX_RESULT_ELEMENTS = 6;
export const ELEMENTS_TYPE_LIST = {
    [ResultSetElementType.Table]: {
        type: ResultSetElementType.Table,
        text: "Tabular View",
    },
    [ResultSetElementType.LineGraph]: {
        type: ResultSetElementType.LineGraph,
        text: "Line Graph",
    },
    [ResultSetElementType.SpaceViewer]: {
        type: ResultSetElementType.SpaceViewer,
        text: "Space Viewer",
    },
    [ResultSetElementType.ProcessDataViewer]: {
        type: ResultSetElementType.ProcessDataViewer,
        text: "Process Data Viewer",
    },
};
export const rearrangeResultSets = (resultSets, from, to) => {
    const array = [...resultSets];
    const [replacingResultSet] = array.splice(from, 1);
    array.splice(to, 0, replacingResultSet);
    return array;
};
const getAvailableCellModels = (cellModels) => cellModels.filter(({ state: { parameters } }) => Object.keys(parameters || {}).filter((key) => parameters?.[key]?.length > 0).length > 0);
const getCellModelsWithFilteredParameters = (availableCellModels, selectedParameters, byUnits) => {
    const usedCellModelParameters = selectedParameters.filter((parameter) => parameter?.type === ResultElementParameterType.CELL_MODEL);
    return availableCellModels.reduce((acc, item) => {
        if (!item?.state?.parameters) {
            return acc;
        }
        const filteredParameters = Object.keys(item.state.parameters).reduce((acc, key) => {
            const filteredGroupParameters = (item.state.parameters?.[key] || []).filter((item) => !usedCellModelParameters.find(({ name, units }) => item.name === name && (!byUnits || (byUnits && item.units === units))));
            return filteredGroupParameters.length === 0 ? acc : { ...acc, [key]: filteredGroupParameters };
        }, {});
        if (!Object.keys(filteredParameters).length) {
            return acc;
        }
        else {
            return [
                ...acc,
                {
                    ...item,
                    state: { ...item?.state, parameters: filteredParameters },
                },
            ];
        }
    }, []);
};
export const getAvailableFilteredCellModels = (cellModels, selectedParameters, byUnits = true) => {
    const availableCellModels = getAvailableCellModels(cellModels);
    return {
        availableCellModels,
        cellModelsWithFilteredParameters: getCellModelsWithFilteredParameters(availableCellModels, selectedParameters, byUnits),
    };
};
export const getAvailableRecipes = (recipes) => recipes.filter((recipe) => recipe.state.parameters.length > 0);
/**
 * this function must be removed when all result elements are ready to work with group of alternative flows
 * and replaced with getResultElementRecipeParameterAndAlternativeStepsItems() from common.utils
 */
const getRecipesWithFilteredParameters = (availableRecipes, selectedParameters, parameterTypes, byUnits) => {
    const usedRecipesParameters = selectedParameters.filter((parameter) => parameter?.type === ResultElementParameterType.RECIPE);
    return availableRecipes.reduce((arr, item) => {
        const recipe = { ...item, state: { ...item.state, parameters: [] } };
        item.state.parameters.forEach((parameter) => {
            const { name, units } = parameter;
            if (!byUnits) {
                if (!usedRecipesParameters.find((parameter) => name === parameter.name)) {
                    recipe.state.parameters.push(parameter);
                }
            }
            else {
                if (!units)
                    return;
                const unitsCategory = getUnitsCategory(parameterTypes, units);
                const usedParameterUnits = usedRecipesParameters
                    .filter((parameter) => parameter.name === name &&
                    parameter.units &&
                    getUnitsCategory(parameterTypes, parameter.units)?.category === unitsCategory?.category)
                    .map(({ units }) => units);
                if (!usedParameterUnits.length || !unitsCategory) {
                    recipe.state.parameters.push(parameter);
                }
                else {
                    const allUnitsUsed = unitsCategory.variablesList.every(({ type }) => usedParameterUnits.includes(type));
                    if (allUnitsUsed) {
                        return;
                    }
                    else {
                        recipe.state.parameters.push(parameter);
                    }
                }
            }
        });
        return [...arr, ...(recipe.state.parameters.length ? [recipe] : [])];
    }, []);
};
export const getAvailableFilteredRecipes = (recipes, selectedParameters, parameterTypes, byUnits = true) => {
    const availableRecipes = getAvailableRecipes(recipes);
    return {
        availableRecipes,
        recipesWithFilteredParameters: getRecipesWithFilteredParameters(availableRecipes, selectedParameters, parameterTypes, byUnits),
    };
};
export const getRecipeParameterUnitsItems = (parameterName, units, parameterTypes, selectedParameters, suppressGroupTitle = false) => {
    const unitsCategory = getUnitsCategory(parameterTypes, units);
    const usedParameterUnits = selectedParameters.filter((parameter) => parameter?.type === ResultElementParameterType.RECIPE &&
        parameter?.name === parameterName &&
        getUnitsCategory(parameterTypes, parameter?.units)?.category === unitsCategory?.category).map((parameter) => parameter?.units);
    const unitsToShow = (unitsCategory?.variablesList ?? []).filter(({ type }) => !usedParameterUnits.includes(type));
    if (unitsToShow.length) {
        const groupTitle = {
            value: "",
            label: unitsCategory?.categoryName ?? "",
            disabled: true,
        };
        const items = unitsToShow.map(({ type }) => {
            const { label } = findDictionaryItemByValue(recipeParameterUnitsType, type) ?? {};
            return {
                value: type,
                label: label ?? type,
            };
        });
        return suppressGroupTitle ? items : [groupTitle, ...items];
    }
    else {
        return [];
    }
};
export const getProcessVariablesToSelect = (processVariables, customVariables, customVariableFilterFn) => {
    const processVariablesOptions = processVariables.reduce((acc, { categoryName, category, variablesList }) => {
        const groupNameItem = {
            value: category,
            label: categoryName,
            disabled: true,
            isGroupName: true,
            groupId: category,
        };
        const variableOptions = variablesList.map((variable) => ({
            value: { ...variable, type: CustomVariableRelativeValueVariableType.ProcessVariable },
            label: variable.name,
            groupId: category,
        }));
        return [...acc, groupNameItem, ...variableOptions];
    }, []);
    const customVariableGroupName = {
        value: CustomVariableRelativeValueVariableType.CustomVariable,
        label: "Custom Variables",
        isGroupName: true,
        disabled: true,
        groupId: CustomVariableRelativeValueVariableType.CustomVariable,
    };
    const filteredCustomVariablesOptions = customVariableFilterFn
        ? (customVariables ?? []).filter(customVariableFilterFn)
        : (customVariables ?? []);
    const customVariablesOptions = filteredCustomVariablesOptions.map((variable) => ({
        value: { ...variable, key: variable.id, type: CustomVariableRelativeValueVariableType.CustomVariable },
        label: variable.name ?? "",
        isGroupName: false,
        groupId: CustomVariableRelativeValueVariableType.CustomVariable,
    }));
    return [
        ...processVariablesOptions,
        customVariableGroupName,
        ...customVariablesOptions,
    ];
};
export const getProcessVariableUnitsOptions = (parameterTypes, units) => {
    const { category: unitsCategory, variablesList: unitsVariablesList } = {
        ...getUnitsCategory(parameterTypes, units),
    };
    if (!unitsCategory || !unitsVariablesList) {
        return [{ value: units, label: units }];
    }
    else {
        return unitsVariablesList.map(({ type }) => findDictionaryItemByValue(recipeParameterUnitsType, type));
    }
};
export const getProcessVariablesUsedUnits = (parameterTypes, selectedParameters, variableType, units) => selectedParameters
    .filter((parameter) => {
    if (parameter.type !== ResultElementParameterType.PROCESS_VARIABLE ||
        parameter.parameter.type !== ProcessVariableType.CellModel) {
        return false;
    }
    const { variableType: usedVariableType, units: usedUnits } = parameter.parameter;
    return (variableType === usedVariableType &&
        getUnitsCategory(parameterTypes, units)?.category ===
            getUnitsCategory(parameterTypes, usedUnits)?.category);
})
    .map((parameter) => parameter?.parameter?.units);
export const getAvailableProcessVariableCategories = (processVariables) => processVariables.filter(({ variablesList }) => !!variablesList.length);
const getFilteredProcessData = (availableProcessData, selectedIsForFilter) => {
    return availableProcessData.filter((data) => !selectedIsForFilter?.includes(data.id));
};
export const getAvailableFilteredProcessData = (processData, selectedIsForFilter) => ({
    availableProcessData: processData,
    filteredProcessData: getFilteredProcessData(processData, selectedIsForFilter),
});
export const getResultVariantsOptions = (simulations, cellModels, recipes, optimizations, selectedSimulationId, selectedOptimizationId, selectedVariantId, selectedSourcesForFilter) => {
    const simulation = simulations.find(({ id }) => id === selectedSimulationId);
    const options = getSimulationVariantsOptions(simulations, selectedSimulationId);
    const noOptimizations = !optimizations?.length;
    const completedOptionsWithSelectedVariant = options.reduce((acc, option) => {
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === option.value);
        if (isSimulationVariantCompleted(simulation, cellModels, recipes, variant) ||
            selectedVariantId === option.value) {
            return [
                ...acc,
                { ...option, disabled: !isSimulationVariantCompleted(simulation, cellModels, recipes, variant) },
            ];
        }
        return acc;
    }, []);
    if (!selectedSourcesForFilter) {
        return completedOptionsWithSelectedVariant;
    }
    return completedOptionsWithSelectedVariant.filter(({ value }) => {
        const selectedSimulationVariantAlreadyUsed = selectedSourcesForFilter?.some((source) => source.simulationId === selectedSimulationId && source.adjustmentId === value);
        const variantOptimizationPairAlreadyUsed = selectedSourcesForFilter?.some(({ adjustmentId, optimizationId, simulationId }) => simulationId === selectedSimulationId &&
            adjustmentId === value &&
            isNotEmpty(selectedOptimizationId) &&
            optimizationId === selectedOptimizationId);
        if (selectedVariantId === value)
            return true;
        return noOptimizations ? !selectedSimulationVariantAlreadyUsed : !variantOptimizationPairAlreadyUsed;
    });
};
export const getPossibleSimulationVariantOptimizationPairs = (simulation, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => {
    const optimizationOptions = getResultElementSourceOptimizationOptions(simulation.id, undefined, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
    const defaultVariantCombinationsWithOptimizations = optimizationOptions?.map(({ value }) => `${simulation.id}-${0}-${value}`);
    const defaultVariantWithoutOptimization = `${simulation.id}-${0}-${null}`;
    const possibleSimulationVariantPairs = simulation?.state?.adjustments?.reduce((acc, variant) => {
        const simulationVariantWithoutOptimization = `${simulation.id}-${variant.id}-${null}`;
        const simulationVariantCombinationsWithOptimizations = optimizationOptions?.map((optimizationOption) => `${simulation.id}-${variant.id}-${optimizationOption.value}`);
        return [...acc, simulationVariantWithoutOptimization, ...simulationVariantCombinationsWithOptimizations];
    }, defaultVariantCombinationsWithOptimizations);
    return [defaultVariantWithoutOptimization, ...(possibleSimulationVariantPairs ?? [])];
};
export const getAvailableSimulations = (simulations, cellModels, recipes) => {
    return simulations.filter((simulation) => isSimulationCompleted(simulation, cellModels, recipes));
};
const getFilteredSimulations = (availableSimulations, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes, selectedSourcesForFilter) => {
    if (!selectedSourcesForFilter) {
        return availableSimulations;
    }
    const simulationRows = selectedSourcesForFilter?.map(({ simulationId, adjustmentId, optimizationId }) => `${simulationId}-${adjustmentId}-${optimizationId ?? null}`);
    return availableSimulations.filter((simulation) => {
        const possibleSimulationVariantPairs = getPossibleSimulationVariantOptimizationPairs(simulation, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
        return !possibleSimulationVariantPairs.every((key) => simulationRows?.includes(key));
    });
};
export const getAvailableFilteredSimulations = (simulations, optimizations, insights, cellModels, recipes, processVariables, incompleteSimulationsIds, selectedSourcesForFilter) => {
    const availableSimulations = getAvailableSimulations(simulations, cellModels, recipes);
    return {
        availableSimulations,
        filteredSimulations: getFilteredSimulations(availableSimulations, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes, selectedSourcesForFilter),
    };
};
export const prepareResultSetsStateOnChangeElement = (currentState, payload) => {
    const resultSets = currentState.state.resultSets;
    const resultSetIndex = resultSets.findIndex(({ id }) => payload.resultSetId === id);
    const resultSet = resultSets[resultSetIndex];
    if (resultSetIndex !== -1 && resultSet) {
        const resultElementIndex = resultSet.elements.findIndex(({ id, type }) => id === payload.id && type === payload.type);
        if (resultElementIndex !== -1) {
            resultSet.elements.splice(resultElementIndex, 1, payload);
            return resultSets;
        }
        else {
            resultSet.elements.push(payload);
            return resultSets;
        }
    }
    else {
        return resultSets;
    }
};
export const sortRowsAndColumns = (element) => ({
    ...element,
    rows: [...(element?.rows || [])].sort((a, b) => a.position - b.position),
    columns: [...(element?.columns || [])].sort((a, b) => a.position - b.position),
});
export const prepareTabularResultsItem = (calculationTabularResults, payload, element, error) => {
    const resultItemIndex = calculationTabularResults.findIndex((item) => compareResultItemIds(item, {
        resultSetId: payload.resultSetId,
        resultSetElementId: payload.resultSetElementId,
    }));
    if (resultItemIndex === -1) {
        return [
            ...calculationTabularResults,
            {
                resultSetId: payload.resultSetId,
                elementType: ResultSetElementType.Table,
                resultSetElementId: payload.resultSetElementId,
                rows: payload.rows ||
                    element?.rows.map(({ id }) => ({
                        rowId: id,
                        state: SimulationRunState.STARTED,
                        columns: [],
                        fetching: false,
                    })) ||
                    [],
            },
        ];
    }
    else {
        let state = SimulationRunState.STARTED;
        if (error) {
            state = SimulationRunState.ERROR;
        }
        calculationTabularResults[resultItemIndex] = {
            ...calculationTabularResults[resultItemIndex],
            rows: payload.rows.map((row) => {
                const existingRow = calculationTabularResults[resultItemIndex].rows.find(({ rowId }) => rowId === row.rowId);
                return {
                    ...existingRow,
                    ...row,
                    state: row?.state || state,
                    fetching: row?.state ? !calculationFinishedStates.includes(row?.state) : false,
                };
            }),
        };
        return calculationTabularResults;
    }
};
export const prepareSpaceviewerResultsItem = (calculationSpaceviewerResults, payload, error) => {
    const { resultSetElementId, resultSetId } = payload;
    const resultItemIndex = calculationSpaceviewerResults.findIndex((item) => compareResultItemIds(item, {
        resultSetId,
        resultSetElementId,
    }));
    if (resultItemIndex === -1) {
        return [
            ...calculationSpaceviewerResults,
            {
                ...payload,
                resultSetId,
                resultSetElementId,
                sources: payload.sources.map((source) => ({
                    sourceId: source.sourceId,
                    state: source.state || SimulationRunState.STARTED,
                    elementType: ResultSetElementType.SpaceViewer,
                    fetching: source.fetching || false,
                    points: source.points || [],
                    parameterIdsSequence: source.parameterIdsSequence || [],
                    accuracy: source.accuracy ?? null,
                })),
            },
        ];
    }
    else {
        let state = SimulationRunState.STARTED;
        if (error) {
            state = SimulationRunState.ERROR;
        }
        calculationSpaceviewerResults[resultItemIndex] = {
            ...calculationSpaceviewerResults[resultItemIndex],
            sources: payload.sources.map((source) => {
                const existingSource = calculationSpaceviewerResults[resultItemIndex].sources.find(({ sourceId }) => source.sourceId === sourceId) || getEmptySpaceviewerResultSource(0);
                return {
                    ...existingSource,
                    ...source,
                    state: source.state || state,
                    points: source.points || [],
                    parameterIdsSequence: source.parameterIdsSequence || [],
                    fetching: typeof source.fetching === "boolean"
                        ? source.fetching
                        : source.state
                            ? !calculationFinishedStates.includes(source.state)
                            : false,
                };
            }),
        };
        return calculationSpaceviewerResults;
    }
};
export const prepareLineGraphResultsItem = (graphResults, payload, error) => {
    const resultItemIndex = graphResults.findIndex((item) => compareResultItemIds(item, {
        resultSetId: payload.resultSetId,
        resultSetElementId: payload.resultSetElementId,
    }));
    if (resultItemIndex === -1) {
        return [
            ...graphResults,
            {
                ...payload,
                results: payload.results.map((item) => ({ ...item, fetching: item.fetching || false })),
            },
        ];
    }
    else {
        let state = SimulationRunState.STARTED;
        if (error) {
            state = SimulationRunState.ERROR;
        }
        graphResults[resultItemIndex] = {
            ...graphResults[resultItemIndex],
            results: payload.results.map((result) => {
                const existingSource = graphResults[resultItemIndex].results.find(({ sourceId }) => sourceId === result.sourceId);
                return {
                    ...existingSource,
                    ...result,
                    fetching: typeof result.fetching === "boolean"
                        ? result.fetching
                        : result.state
                            ? !calculationFinishedStates.includes(result.state)
                            : false,
                    state: result?.state || state,
                    points: result.points || [],
                };
            }),
        };
        return graphResults;
    }
};
export const prepareProcessDataViewerResultsItem = (graphResults, payload, element, isError = false, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData, parameterTypes) => {
    const { resultSetId, resultSetElementId } = payload;
    const resultItemIndex = graphResults.findIndex((item) => compareResultItemIds(item, { resultSetId, resultSetElementId }));
    if (resultItemIndex === -1) {
        return [
            ...graphResults,
            {
                resultSetId,
                resultSetElementId,
                results: payload.results.map((item) => ({
                    ...omit(item, "points"),
                    fetching: item.fetching || false,
                    median: null,
                    average: null,
                    errorsGraph: null,
                    points: null,
                    medianSpread: [],
                    averageSpread: [],
                })),
                timeUnit: null,
            },
        ];
    }
    else {
        let state = SimulationRunState.STARTED;
        if (isError) {
            state = SimulationRunState.ERROR;
        }
        const maxUnit = getTimeUnit(getArrayMinMax(payload.results?.map(({ points }) => points?.[points.length - 1]?.point?.x ?? 0))?.max);
        const timeUnit = maxUnit ?? TimeUnits.hours;
        graphResults[resultItemIndex] = {
            ...graphResults[resultItemIndex],
            timeUnit,
            results: payload.results.map((result) => {
                const existingSource = graphResults[resultItemIndex].results.find(({ sourceId }) => sourceId === result.sourceId);
                const source = element?.sources?.find(({ id }) => id === result.sourceId);
                const sourceLineTitle = source?.source?.type === ResultElementSourceType.Insight
                    ? (getResultElementSourceComputedName(source?.source, simulations || [], optimizations || [], insights || [], recipes || [], persistedInsightsData || [], processVariables || [], processData || []) ?? "")
                    : (getGraphElementSourceVariableName(source?.source || null, simulations || [], recipes || [], getProcessVariablesOptions(processVariables || [])) ?? "");
                const units = getLineGraphSourceUnitOption(source?.source || null, simulations || [], recipes || [], parameterTypes || null);
                const unitsPrefix = getDefaultUnitsPrefix(units?.value);
                let sourceLineUnits = units?.label ?? "";
                if (sourceLineUnits && unitsPrefix) {
                    sourceLineUnits = `${unitsPrefix} ${sourceLineUnits}`;
                }
                const sourceLineComputedName = source?.source?.type === ResultElementSourceType.Insight
                    ? ""
                    : getResultElementSourceComputedName(source?.source ?? null, simulations ?? [], optimizations ?? [], insights ?? [], recipes ?? [], persistedInsightsData ?? [], processVariables ?? [], processData ?? []);
                const { median, medianSpread, average, averageSpread, errorsGraph } = getProcessDataViewerTraces(result, source, timeUnit, sourceLineTitle, sourceLineUnits, sourceLineComputedName);
                return {
                    ...existingSource,
                    ...omit(result, "points"),
                    fetching: typeof result.fetching === "boolean"
                        ? result.fetching
                        : result.state
                            ? !calculationFinishedStates.includes(result.state)
                            : false,
                    state: result?.state || state,
                    median,
                    medianSpread,
                    average,
                    averageSpread,
                    errorsGraph,
                    points: result.points ?? null,
                };
            }),
        };
        return graphResults;
    }
};
export const prepareProcessDataViewerGraphData = (results, sources) => results?.reduce((data, result, index) => {
    const source = sources?.find(({ id }) => result.sourceId === id);
    const sourceColor = getSourceLineColor(source?.color, index);
    const visibility = !!source?.view;
    switch (source?.viewType) {
        case ResultElementGraphViewType.MedianWithSpread:
            return getGraphDataWithSpread("median", data, result, sourceColor, visibility, index);
        case ResultElementGraphViewType.MeanWithSpread:
            return getGraphDataWithSpread("average", data, result, sourceColor, visibility, index);
        default: {
            data.push(getDefaultGraphData(result, sourceColor, visibility, index));
            return data;
        }
    }
}, []);
export const prepareProcessDataViewerGraphErrorsData = (result, source, index) => {
    if (index === undefined) {
        return [];
    }
    const sourceColor = getSourceLineColor(source?.color, index);
    const visibility = !!source?.view;
    const errorsTrace = getErrorsTrace(false);
    errorsTrace.x = result?.errorsGraph?.x ?? [];
    errorsTrace.y = result?.errorsGraph?.y ?? [];
    errorsTrace.customdata = (result?.errorsGraph?.customdata ?? []);
    const data = [errorsTrace];
    switch (source?.viewType) {
        case ResultElementGraphViewType.MedianWithSpread:
            return getGraphDataWithSpread("median", data, result, sourceColor, visibility, 0);
        case ResultElementGraphViewType.MeanWithSpread:
            return getGraphDataWithSpread("average", data, result, sourceColor, visibility, 0);
        default: {
            data.push(getDefaultGraphData(result, sourceColor, visibility, 0));
            return data;
        }
    }
};
export const prepareTableResultPayloadWithSortedRowsAndColumns = (payload) => ({
    ...payload,
    columns: payload.columns.sort((a, b) => a.position - b.position),
    rows: payload.rows.sort((a, b) => a.position - b.position),
});
export const isScenarioItemParameterUsedAsColumn = (element, scenarioItem, type) => {
    if (type === ScenarioNodeType.CELLMODEL) {
        const { state: { parameters } = {} } = scenarioItem;
        for (const column of element.columns) {
            if (column.parameter.type === ResultElementParameterType.CELL_MODEL) {
                for (const parameterGroupName of Object.keys(parameters || {})) {
                    for (const parameter of (parameters || {})[parameterGroupName]) {
                        if (parameter.name === column.parameter.name) {
                            return true;
                        }
                    }
                }
            }
        }
    }
    if (type === ScenarioNodeType.RECIPE) {
        const { state: { parameters } = {} } = scenarioItem;
        for (const column of element.columns) {
            if (column.parameter.type === ResultElementParameterType.RECIPE) {
                for (const parameter of parameters || []) {
                    if (parameter.name === column.parameter.name) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
};
const isSimulationUsedInInsightResultElementSource = (source, insights, simulationId) => {
    if (!simulationId || source?.type !== ResultElementSourceType.Insight)
        return false;
    const insight = insights?.find(({ id }) => id === source?.insightId);
    return insight?.state?.input?.inputSets?.some(({ inputs }) => inputs?.some(({ inputId, inputType }) => inputType === InsightInputType.SIMULATION && inputId === simulationId));
};
export const isSimulationVariantUsedInResultElement = (resultSets, simulationId, variantId) => resultSets.some((resultSet) => resultSet.elements?.some((element) => {
    switch (element.type) {
        case ResultSetElementType.Table:
            return element.rows?.some((row) => row.sourceType === TableElementSourceType.SIMULATION &&
                row.sourceId === simulationId &&
                row.adjustmentId === variantId);
        case ResultSetElementType.SpaceViewer:
            return element.sources?.some((source) => {
                const sourceSource = source.source;
                return (sourceSource?.type === ResultElementSourceType.Simulation &&
                    sourceSource?.id === simulationId &&
                    sourceSource?.adjustmentId === variantId);
            });
        case ResultSetElementType.ProcessDataViewer:
            return element.sources?.some((source) => {
                const sourceSource = source.source;
                return (sourceSource?.type === ResultElementSourceType.Simulation &&
                    sourceSource?.simulationId === simulationId &&
                    sourceSource?.adjustmentId === variantId);
            });
        case ResultSetElementType.LineGraph:
            return element.sources?.some((source) => {
                const sourceSource = source.source;
                return (sourceSource?.type === ResultElementSourceType.Simulation &&
                    sourceSource?.simulationId === simulationId &&
                    sourceSource?.adjustmentId === variantId);
            });
    }
}));
export const getResultElementsUsingSimulation = (resultSet, insights, currentSimulationId) => resultSet.elements?.filter((element) => {
    switch (element.type) {
        case ResultSetElementType.Table:
            return element.rows?.some((row) => row.sourceType === TableElementSourceType.SIMULATION && row.sourceId === currentSimulationId);
        case ResultSetElementType.SpaceViewer:
            return element.sources?.some((source) => {
                const simulationUsedInInsight = source.source?.type === ResultElementSourceType.Insight &&
                    isSimulationUsedInInsightResultElementSource(source?.source, insights, currentSimulationId);
                return ((source.source?.type === ResultElementSourceType.Simulation &&
                    source.source.id === currentSimulationId) ||
                    simulationUsedInInsight);
            });
        case ResultSetElementType.ProcessDataViewer:
            return element.sources?.some((source) => {
                const simulationUsedInInsight = source.source?.type === ResultElementSourceType.Insight &&
                    isSimulationUsedInInsightResultElementSource(source?.source, insights, currentSimulationId);
                return ((source.source?.type === ResultElementSourceType.Simulation &&
                    source.source?.simulationId === currentSimulationId) ||
                    simulationUsedInInsight);
            });
        case ResultSetElementType.LineGraph:
            return element.sources?.some((source) => {
                const simulationUsedInInsight = source.source?.type === ResultElementSourceType.Insight &&
                    isSimulationUsedInInsightResultElementSource(source?.source, insights, currentSimulationId);
                return ((source.source?.type === ResultElementSourceType.Simulation &&
                    source.source?.simulationId === currentSimulationId) ||
                    simulationUsedInInsight);
            });
    }
});
export const getResultElementsUsingInsight = (resultSet, currentInsightId) => resultSet.elements?.filter((element) => {
    switch (element.type) {
        case ResultSetElementType.Table:
            return element.columns?.some((column) => column.parameter.type === ResultElementParameterType.INSIGHT &&
                String(column.parameter.insightId) === String(currentInsightId));
        case ResultSetElementType.SpaceViewer:
            return element.sources?.some((source) => source.source?.type === ResultElementSourceType.Insight &&
                source.source?.insightId === currentInsightId);
        case ResultSetElementType.ProcessDataViewer:
            return element.sources?.some((source) => source.source?.type === ResultElementSourceType.Insight &&
                source.source?.insightId === currentInsightId);
        case ResultSetElementType.LineGraph:
            return element.sources?.some((source) => source.source?.type === ResultElementSourceType.Insight &&
                source.source?.insightId === currentInsightId);
    }
});
export const getResultElementsUsingOptimization = (resultSet, currentOptimizationId) => {
    return resultSet.elements?.filter((element) => {
        switch (element.type) {
            case ResultSetElementType.Table:
                return element.rows?.some((row) => {
                    const hasSelectedOptimization = row.optimizationId !== null;
                    return (hasSelectedOptimization &&
                        row.sourceType === TableElementSourceType.SIMULATION &&
                        row.optimizationId === currentOptimizationId);
                });
            case ResultSetElementType.ProcessDataViewer:
            case ResultSetElementType.LineGraph:
                return element.sources?.some((source) => source.source?.type === ResultElementSourceType.Simulation &&
                    source.source?.optimizationId === currentOptimizationId);
        }
    });
};
export const getResultElementSourceOptimizationOptions = (simulationId, variantId, optimizations, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes, currentOptimizationId) => optimizations.reduce((acc, optimization) => {
    const isCompleted = isOptimizationCompleted(optimization.state, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
    const targetInsight = insights.find((insight) => insight.id === optimization.state.input?.insightId);
    const targetInsightInputSet = !!targetInsight &&
        targetInsight.state.input.inputSets.find((inputSet) => inputSet.id === optimization.state.input?.insightInputSetId);
    const isSimulationVariantUsedInInput = !!targetInsightInputSet &&
        targetInsightInputSet.inputs.some(({ inputId, inputType, adjustmentId }) => inputType === InsightInputType.SIMULATION &&
            inputId === simulationId &&
            (variantId === undefined ? true : adjustmentId === variantId));
    if (!isSimulationVariantUsedInInput || (!isCompleted && optimization.id !== currentOptimizationId)) {
        return acc;
    }
    return [
        ...acc,
        {
            value: optimization.id,
            label: optimization.name ?? "",
            disabled: optimization.id === currentOptimizationId,
        },
    ];
}, []);
export const getSpaceviewerSourceName = (source, simulations, insights, persistedInsightsData, processVariables, recipes) => {
    if (!source.source) {
        return "";
    }
    switch (source.source.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, adjustmentId, id } = { ...source?.source };
            return getSimulationSourceName(simulations, undefined, simulationId ?? id, undefined, adjustmentId);
        }
        case ResultElementSourceType.Insight: {
            const { insightId, inputSetId, output } = { ...source?.source };
            return getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes);
        }
    }
};
export const prepareSpaceviewerSource = (source, variableOptionsList, parameterTypes, simulations, insights, persistedInsights, processVariables, recipes) => {
    const sourceName = getSpaceviewerSourceName(source, simulations, insights, persistedInsights, processVariables, recipes);
    const base = {
        ...source,
        [SourceFields.type]: source.source?.type,
        [SourceFields.source]: isNotEmpty(sourceName, true)
            ? { value: sourceName || "", label: sourceName || "" }
            : null,
    };
    if (source.source?.type === ResultElementSourceType.Simulation) {
        const { id: simulationId, variableType, units, timing, customVariableId, } = {
            ...source.source,
        };
        const recipeId = simulations?.find((item) => item.id === simulationId)?.recipeId;
        const customVariables = getRecipesCustomVariables(recipes, [recipeId]);
        const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
        const customVariableUnit = parseComplexUnitToHtml(customVariableEntity?.formula?.configuredUnits?.complexValue)?.html;
        const variableName = variableOptionsList.find((item) => item.value === variableType)?.label;
        const variableOption = { value: variableType ?? "", label: variableName };
        const customVariableOption = { value: customVariableId ?? 0, label: customVariableEntity?.name ?? "" };
        const unitOption = customVariableId
            ? { value: customVariableUnit, label: customVariableUnit }
            : getRecipeParameterUnitsItems("", units, parameterTypes, []).find((item) => item.value === units);
        return {
            ...base,
            [SourceFields.simulation_id]: source.source.id,
            [SourceFields.adjustmentId]: source.source.adjustmentId,
            [SourceFields._variableName]: variableType ? variableOption : customVariableOption,
            [SourceFields.variableType]: isNotEmpty(variableType, true) ? variableOption : undefined,
            [SourceFields.customVariableId]: isNotEmpty(customVariableId, true) ? customVariableOption : undefined,
            [SourceFields.units]: unitOption,
            [SourceFields.timing]: timing,
        };
    }
    if (source.source?.type === ResultElementSourceType.Insight) {
        const { inputSetId, insightId, output: { id: outputId, isFinal }, } = {
            ...source.source,
        };
        return {
            ...base,
            [SourceFields.insightId]: insightId,
            [SourceFields.inputSetId]: inputSetId,
            [SourceFields.outputId]: outputId ?? 0,
            [SourceFields.isFinal]: isFinal,
        };
    }
    return base;
};
export const getGraphElementSourceError = (source, persistedSimulationPreviews, persistedInsightsData, optimizations, incompleteSimulationsIds, processVariables, insights, simulations, cellModels, recipes) => {
    let sourceError = "";
    switch (source?.source?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, adjustmentId, optimizationId } = {
                ...source?.source,
            };
            const simulation = simulations?.find(({ id }) => id === simulationId);
            const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
            const simulationSourceIsNotComplete = incompleteSimulationsIds.includes(simulationId ?? 0);
            const simulationVariantIncomplete = !simulationSourceIsNotComplete &&
                !isSimulationVariantCompleted(simulation, cellModels, recipes, variant);
            const combinedId = `${simulationId}-${0}`;
            const simulationSourceErrored = !!persistedSimulationPreviews?.[combinedId]?.graphData?.errorMessage;
            if (optimizationId) {
                const optimization = optimizations?.find(({ id }) => id === optimizationId);
                const { insightId, insightInputSetId, outputId } = { ...optimization?.state?.input };
                const optimizationSourceIsNotComplete = !isOptimizationCompleted(optimization?.state, insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
                const optimizationErrored = checkOptimizationErrored(insightId, insightInputSetId, outputId, persistedInsightsData);
                if (optimizationErrored || optimizationSourceIsNotComplete) {
                    sourceError = INCOMPLETE_ENTITIES_MESSAGES.OPTIMIZATION_INCOMPLETE;
                    break;
                }
            }
            if (simulationSourceIsNotComplete && !simulationVariantIncomplete) {
                sourceError = INCOMPLETE_ENTITIES_MESSAGES.SIMULATION_INCOMPLETE;
            }
            if (simulationVariantIncomplete) {
                sourceError = INCOMPLETE_ENTITIES_MESSAGES.SIMULATION_VARIANT_INCOMPLETE;
            }
            if (simulationSourceErrored) {
                sourceError = persistedSimulationPreviews?.[combinedId]?.graphData?.errorMessage ?? "";
            }
            break;
        }
        case ResultElementSourceType.Insight: {
            const { insightId, inputSetId, output } = { ...source?.source };
            const insightSourceErrored = checkInsightHasInvalidScore(insightId, inputSetId, output?.id, output?.isFinal, persistedInsightsData);
            if (insightSourceErrored) {
                sourceError = INCOMPLETE_ENTITIES_MESSAGES.INSIGHT_INCOMPLETE;
            }
            break;
        }
        default:
            break;
    }
    return sourceError;
};
export const getResultElementSourceTypeLabel = (sourceType, optimizationId) => {
    switch (sourceType) {
        case ResultElementSourceType.Simulation:
            return isNotEmpty(optimizationId) ? "Optimization" : "Simulation";
        case ResultElementSourceType.Insight:
            return "Insight";
        case ResultElementSourceType.ProcessData:
            return "Process Data";
        default:
            return undefined;
    }
};
const getGraphElementSourceVariableName = (sourceSource, simulations, recipes, variableOptionsList) => {
    switch (sourceSource?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, customVariableId, variableType } = {
                ...sourceSource,
            };
            const simulation = simulations?.find(({ id }) => id === simulationId);
            const customVariables = getRecipesCustomVariables(recipes, [simulation?.recipeId]);
            const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
            const processVariableName = variableOptionsList.find((item) => item.value === variableType)?.label ?? "";
            return customVariableId ? (customVariableEntity?.name ?? "") : processVariableName;
        }
        case ResultElementSourceType.ProcessData: {
            const { variableType, liquid } = { ...sourceSource };
            const processVariableName = variableOptionsList.find((item) => item.value === variableType)?.label ?? "";
            return liquid ? liquid : processVariableName;
        }
        default:
            return undefined;
    }
};
export const getGraphElementSourceFormValues = (source, parameter, cellModels, recipes, simulations, incompleteSimulationsIds, insights, persistedInsightsData, optimizations, processData, variableOptionsList, parameterTypes, processVariables) => {
    let payloadVariableType = "";
    let sourceSourcePayload = {};
    if (source?.source?.type === ResultElementSourceType.Simulation) {
        const { variableType, timing } = {
            ...source.source,
        };
        payloadVariableType = variableType;
        sourceSourcePayload.timing = timing;
    }
    if (source?.source?.type === ResultElementSourceType.Insight) {
        const { output } = { ...source.source };
        sourceSourcePayload[SourceSourceFields.outputId] = output?.id;
    }
    if (source?.source?.type === ResultElementSourceType.ProcessData) {
        const { variableType } = { ...source.source };
        payloadVariableType = variableType;
    }
    const variableName = getGraphElementSourceVariableName(source.source, simulations, recipes, variableOptionsList);
    sourceSourcePayload = {
        ...sourceSourcePayload,
        [SourceSourceFields._sourceName]: getResultElementSourceComputedName(source?.source, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData),
        ...(source?.source?.type === ResultElementSourceType.Insight
            ? {}
            : {
                [SourceSourceFields._variableName]: variableName
                    ? { value: variableName, label: variableName }
                    : undefined,
                [SourceSourceFields.units]: getLineGraphSourceUnitOption(source?.source, simulations, recipes, parameterTypes),
                [SourceSourceFields.variableType]: isNotEmpty(payloadVariableType, true)
                    ? payloadVariableType
                    : undefined,
            }),
    };
    const { usedSimulations } = getUsedTargetsFromSource(source?.source, cellModels, recipes, simulations, insights);
    const { set } = parameter?.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW
        ? getAlternativeStepCompleteness(parameter.parameter.name, usedSimulations, recipes)
        : parameter?.parameter?.type === ResultElementParameterType.RECIPE
            ? getRecipeParameterCompleteness(parameter.parameter.name, usedSimulations, recipes)
            : getCellModelParameterCompleteness(parameter?.parameter?.name, usedSimulations, cellModels);
    return {
        preparedSource: {
            ...source,
            source: !source?.source ? null : { ...source.source, ...sourceSourcePayload },
        },
        hasNoParameters: !set || !isParameterDimensionCompleted(parameter ?? {}),
        sourceTypeLabel: getResultElementSourceTypeLabel(source?.source?.type, source.source?.optimizationId),
        isFilled: parameter === null
            ? isProcessDataViewerSourceCompleted(source)
            : isLineGraphSourceCompleted(source, processVariables),
        isCompleted: isProcessDataViewerSourceSourceCompleted(source, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes),
    };
};
export const getSimulationTimestampOptions = (simulationId, simulations, recipes) => {
    const sourceRecipeId = simulations.find(({ id }) => id === simulationId)?.recipeId;
    const recipeSteps = flatSteps(recipes.find(({ id }) => id === sourceRecipeId)?.state?.steps ?? []);
    return getRecipeTimestamps(recipeSteps);
};
export const getTimingSummaryText = (timing, timestampOptions, processVariables, customVariables) => {
    const timestampLabel = getSelectedTimestamp(timestampOptions, timing ?? {});
    const cutEventLabel = timing?.cutEvent && getCutEventLabel(processVariables, timing?.cutEvent, customVariables);
    const offsetUnits = findDictionaryItemByValue(timeUnits, timing?.offsetUnit)?.label;
    let label = "";
    if (timestampLabel && (!timing?.cutEvent || (timing?.cutEvent && cutEventLabel))) {
        label = timestampLabel;
        if (isNotEmpty(timing?.offset) && offsetUnits) {
            const offsetLabel = timing?.offset === 0 ? "" : ` (${timing?.offset} ${offsetUnits})`;
            label = `${label}${offsetLabel}${cutEventLabel ?? ""}`;
        }
    }
    return label;
};
export const getSortedPointsByDensity = (points) => points.slice().sort((a, b) => {
    return b.response.densityRank - a.response.densityRank;
});
export const isResultElementEmpty = (element) => {
    switch (element.type) {
        case ResultSetElementType.Table: {
            const { rows, columns } = element;
            return rows.length < 1 && columns.length < 1;
        }
        case ResultSetElementType.SpaceViewer: {
            const { sources, parameters } = element;
            return sources.length < 1 || parameters.length < 1;
        }
        case ResultSetElementType.LineGraph: {
            const { sources, parameter } = element;
            return (sources?.length ?? 0) < 1 || parameter?.parameter === null;
        }
        case ResultSetElementType.ProcessDataViewer: {
            const { sources } = element;
            return (sources?.length ?? 0) < 1;
        }
        default:
            return true;
    }
};
export const prepareSpaceviewerPoints = (parameters, points) => {
    if (parameters.every(({ type }) => type === SpaceviewerAxisType.FLOAT)) {
        return points;
    }
    return points?.map((point) => ({
        ...point,
        values: point.values.map((value, index) => {
            const parameter = parameters[index];
            if (parameter?.type !== SpaceviewerAxisType.INTEGER) {
                return value;
            }
            const { steps, flowIds = [] } = parameter;
            const flowIndex = flowIds.findIndex((idx) => idx === value);
            return flowIndex >= 0 ? steps[flowIndex].value : NaN;
        }),
    }));
};
export const filterSpaceViewerDataByConfidenceAndDensity = (points, source) => {
    const isFullRange = source?.confidenceMin === 0 && source.confidenceMax === 100;
    const isDensityEmpty = source?.density === points?.length || source?.density === 0 || source?.density === null;
    if (!points ||
        !source ||
        (!isNotEmpty(source.confidenceMin) && !isNotEmpty(source.confidenceMax)) ||
        (isFullRange && isDensityEmpty)) {
        return points ?? [];
    }
    if (!isFullRange && isDensityEmpty) {
        return (points?.filter((item) => item.response.confidence >= (source.confidenceMin ?? 0) &&
            item.response.confidence <= (source.confidenceMax ?? 100)) ?? []);
    }
    if (isFullRange && !isDensityEmpty) {
        return points && source.density && getSortedPointsByDensity(points).slice(0, source.density);
    }
    const confidencePoints = points?.filter((item) => item.response.confidence >= (source.confidenceMin ?? 0) &&
        item.response.confidence <= (source.confidenceMax ?? 100)) ?? [];
    return source?.density && getSortedPointsByDensity(confidencePoints).slice(0, source.density);
};
export const transformSpaceviewerElementValues = (values) => ({
    name: values.name,
    parameters: values.parameters,
    sources: values.sources.map(({ id, type, view, variableType, customVariableId, simulationId = 0, adjustmentId, timing = null, units, insightId = 0, inputSetId = 0, outputId = 0, isFinal = false, confidenceMin, confidenceMax, colorProfile, }) => {
        const payloadSource = type === ResultElementSourceType.Simulation
            ? {
                id: simulationId,
                variableType: variableType?.value ?? null,
                customVariableId: customVariableId?.value ?? null,
                timing,
                units: units?.value ?? null,
                type,
                adjustmentId: isNotEmpty(adjustmentId) ? adjustmentId : null,
            }
            : {
                insightId,
                inputSetId,
                output: { id: outputId, isFinal },
                type,
            };
        return {
            id: id ?? 0,
            view,
            colorProfile,
            confidenceMin,
            confidenceMax,
            density: null,
            source: type ? payloadSource : null,
        };
    }),
});
export const transformProcessDataViewerElementValues = (values) => ({
    name: values.name,
    sources: values.sources?.map(({ id, view, source, ...restSource }) => {
        const { type, variableType, customVariableId, adjustmentId, simulationId, optimizationId, processDataId, insightId, inputSetId, outputId, liquid, units, } = {
            ...source,
        };
        const isProcessData = type === ResultElementSourceType.ProcessData;
        const isInsight = type === ResultElementSourceType.Insight;
        let payloadSource = {
            type,
            ...(!isInsight ? { variableType: variableType ?? null, units: units?.value ?? null } : {}),
        };
        if (type === ResultElementSourceType.Simulation) {
            payloadSource = {
                ...payloadSource,
                simulationId,
                optimizationId,
                customVariableId: customVariableId ?? null,
                adjustmentId: isNotEmpty(adjustmentId) ? adjustmentId : null,
            };
        }
        if (isProcessData) {
            payloadSource = {
                ...payloadSource,
                processDataId,
                target: liquid ? ProcessDataEntityTarget.CumulativeLiquid : ProcessDataEntityTarget.ProcessVariable,
                liquid,
            };
        }
        if (isInsight) {
            payloadSource = {
                ...payloadSource,
                insightId,
                inputSetId,
                output: {
                    id: outputId,
                    isFinal: outputId === OUTPUT_FINAL_ID,
                },
            };
        }
        return {
            ...restSource,
            ...(isProcessData ? { viewType: ResultElementGraphViewType.Median } : {}),
            id,
            view,
            source: type ? payloadSource : null,
        };
    }),
});
export const transformLineGraphElementValues = (values) => ({
    name: values.name,
    sources: values.sources?.map(({ id, view, source, ...restSource }) => {
        const { type, variableType, customVariableId, simulationId, adjustmentId, optimizationId, insightId, inputSetId, outputId, units, timing, } = {
            ...source,
        };
        const isInsight = type === ResultElementSourceType.Insight;
        let payloadSource = {
            type,
            ...(!isInsight ? { variableType: variableType ?? null, units: units?.value ?? null } : {}),
        };
        if (type === ResultElementSourceType.Simulation) {
            payloadSource = {
                ...payloadSource,
                simulationId,
                optimizationId: optimizationId ?? null,
                customVariableId: customVariableId ?? null,
                timing,
                adjustmentId: isNotEmpty(adjustmentId) ? adjustmentId : null,
            };
        }
        if (isInsight) {
            payloadSource = {
                ...payloadSource,
                insightId,
                inputSetId,
                output: {
                    id: outputId,
                    isFinal: outputId === OUTPUT_FINAL_ID,
                },
            };
        }
        return {
            ...restSource,
            id,
            view,
            source: type ? payloadSource : null,
        };
    }),
    parameter: values.parameter,
});
export const mapSpaceViewerDataToSourceTemplate = (source) => {
    const { simulationId, id, variableType, customVariableId, units, timing } = {
        ...source?.source,
    };
    const { insightId, inputSetId, output } = { ...source?.source };
    const type = source?.type ?? source?.source?.type;
    return {
        id: source?.source ? source.id : id || undefined,
        type,
        view: source?.view,
        confidenceMin: source?.confidenceMin,
        confidenceMax: source?.confidenceMax,
        density: source?.density,
        source: null,
        colorProfile: source?.colorProfile,
        ...(type === ResultElementSourceType.Simulation
            ? {
                simulationId: source?.source
                    ? (source?.simulationId ?? (simulationId || id))
                    : (source?.simulationId ?? source?.id),
                adjustmentId: source?.adjustmentId,
                variableType: source?.variableType ?? (variableType ? { value: variableType, label: variableType } : null),
                customVariableId: source?.customVariableId ?? (customVariableId ? { value: customVariableId, label: "" } : null),
                timing: source?.timing ?? timing,
                units: source?.units ?? (units ? { value: units, label: units } : null),
            }
            : {
                insightId: source?.insightId ?? insightId,
                inputSetId: source?.inputSetId ?? inputSetId,
                outputId: source?.outputId ?? source?.output?.id ?? output?.id ?? undefined,
                isFinal: source?.isFinal ?? source?.output?.isFinal ?? output?.isFinal ?? undefined,
            }),
    };
};
// return the element from the bottom-right place
export const getLastElement = (elements) => {
    if (elements.length) {
        let element = elements[0];
        elements.forEach((item) => {
            if (item.row > element.row) {
                element = item;
            }
            else if (item.row == element.row && item.column > element.column) {
                element = item;
            }
        });
        return element;
    }
    return undefined;
};
export const checkElementHasFullView = ({ type, fullView }) => fullView || type === ResultSetElementType.SpaceViewer;
export const getNewElementPosition = (element, elements) => {
    const lastElement = getLastElement(elements);
    if (lastElement) {
        if (lastElement.column === 0 &&
            !checkElementHasFullView(lastElement) &&
            !checkElementHasFullView(element)) {
            return { row: lastElement.row, column: 1 };
        }
        if (isNotEmpty(lastElement.row)) {
            return { row: lastElement.row + 1, column: 0 };
        }
    }
    return { row: 0, column: 0 };
};
export const isProcessVariableSubmitDisabled = (parameter) => {
    if (!parameter?.type) {
        return true;
    }
    if (parameter.type === ProcessVariableType.CellModel && !isNotEmpty(parameter.variableType)) {
        return true;
    }
    if (parameter.type === ProcessVariableType.Custom && !isNotEmpty(parameter.customVariableId)) {
        return true;
    }
    if (!parameter.cut?.timestampType) {
        return true;
    }
    if (parameter.cut?.cutEvent) {
        const { operation } = parameter.cut.cutEvent;
        if (!operation || !operation?.type) {
            return true;
        }
        if (operation.type === EventValueType.OUT_OF_RANGE) {
            return [operation.min?.value, operation.min?.units, operation.max?.value, operation.max?.units].some((item) => !isNotEmpty(item));
        }
        else {
            return !operation.value?.units || !isNotEmpty(operation.value?.value);
        }
    }
    return false;
};
export const getSimulationVariantsOptions = (simulations, simulationId) => {
    const simulation = simulations.find(({ id }) => id === simulationId);
    if (!simulationId || !simulation)
        return [];
    return (simulation?.state?.adjustments ?? [])?.reduce((acc, variant) => [...acc, { value: variant.id, label: variant.name ?? "" }], [{ value: 0, label: "Default variant" }]);
};
export const getSourceLineColor = (color, index) => color ?? SOURCE_LINE_COLORS[index ?? 0];
export const isInsightRelatedToProcessData = (processDataId, insightId, insightInputSetId, insights) => {
    const insight = insights.find(({ id }) => insightId === id);
    const inputSet = insight?.state?.input.inputSets.find(({ id }) => insightInputSetId === id);
    return inputSet?.inputs.some(({ inputId, inputType }) => inputType === InsightInputType.PROCESS_DATA && inputId === processDataId);
};
export const getSourceNameTooltipLabel = (sourceTypeLabel, source, appearance, simulations, cellModels, recipes) => {
    if (source?.type === ResultElementSourceType.Simulation) {
        const { simulationId, adjustmentId, customVariableId, id } = {
            ...source,
        };
        if (isNotEmpty(customVariableId) && appearance === ResultElementSourceAppearance.Incomplete) {
            return "Custom Variable";
        }
        const simulation = simulations?.find((item) => item.id === (simulationId ?? id));
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === adjustmentId);
        const variantIncomplete = !!variant && !isSimulationVariantCompleted(simulation, cellModels, recipes, variant);
        if (appearance === ResultElementSourceAppearance.Incomplete &&
            isSimulationCompleted(simulation, cellModels, recipes) &&
            variantIncomplete) {
            return "Simulation variant";
        }
    }
    return sourceTypeLabel;
};
export const getSourceNameTooltip = (appearance, sourceTypeLabel, error, hasNoParameters, isFilled) => {
    switch (appearance) {
        case ResultElementSourceAppearance.Incomplete:
            return `The ${sourceTypeLabel} is no longer valid`;
        case ResultElementSourceAppearance.Error:
            return error || `${sourceTypeLabel} can not be calculated.`;
        case ResultElementSourceAppearance.Ready: {
            if (sourceTypeLabel && hasNoParameters) {
                return `This ${sourceTypeLabel} does not have the specified Parameters.`;
            }
            return isFilled ? "Ready to be calculated" : undefined;
        }
    }
};
const renderExportedTabularResultResultCells = (rowResult, cellResult, status, row, column, flowsList) => {
    let cellContent = "";
    const isNa = cellResult?.state === CellState.NA;
    const hasError = cellResult.errorCount > 0 ||
        cellResult?.state === CellState.ERROR ||
        rowResult?.state === SimulationRunState.ERROR;
    const inProgress = status === CalculationStatusSource.InProgress || rowResult?.fetching;
    const cellValue = cellResult.type === TableCellResultType.STATIC ? cellResult?.value : cellResult?.bar;
    if (inProgress) {
        cellContent = "Pending";
    }
    if (isNa) {
        cellContent = N_A;
    }
    if (hasError && !cellValue) {
        const errorText = cellResult?.error || cellResult?.errors?.[0] || rowResult?.error || "Failed to get result";
        cellContent = errorText.replace(/(\r\n|\r|\n)/g, "");
    }
    if (row.changed || column.changed) {
        cellContent = "";
    }
    if (cellResult.state === CellState.VALUE) {
        switch (cellResult.type) {
            case TableCellResultType.STATIC:
                return [
                    ...Array(5).fill(cellContent),
                    (Array.isArray(flowsList)
                        ? flowsList.find((_, idx) => `${idx}` === cellResult?.value) || cellResult?.value
                        : formatCellModelParameterValue(cellResult?.value))?.replaceAll(",", "") ?? "",
                ];
            case TableCellResultType.SINGLE_WHISKER_BAR: {
                const { min, firstQuartile, median, thirdQuartile, max } = { ...cellResult.bar };
                return cellResult.bar
                    ? [min, firstQuartile, median, thirdQuartile, max, ""].map((item) => String(item))
                    : Array(6).fill(cellContent);
            }
        }
    }
    return Array(6).fill(cellContent);
};
const renderExportedProcessDataViewerPointCells = (sourceResult, sourceError, calculationRow, pointData, flowsList) => {
    const RESULT_CELLS_COUNT = 5;
    const missed = sourceResult?.state === SimulationRunState.MISSED;
    const pending = !!sourceResult?.fetching || calculationRow?.status === CalculationStatusSource.InProgress;
    const { point, errorCount = 0 } = { ...pointData };
    const errored = errorCount > 0 || sourceResult?.state === SimulationRunState.ERROR || !!sourceError;
    if (pending) {
        return Array(RESULT_CELLS_COUNT).fill("Pending");
    }
    if (errored && point?.median === null) {
        const errorText = sourceResult?.error || sourceResult?.errors?.[0] || sourceError;
        return Array(RESULT_CELLS_COUNT).fill(errorText?.replace(/(\r\n|\r|\n)/g, ""));
    }
    if (missed) {
        return Array(RESULT_CELLS_COUNT).fill(N_A);
    }
    if (!point) {
        return Array(RESULT_CELLS_COUNT).fill("");
    }
    const x = Array.isArray(flowsList) ? flowsList.find((_, idx) => idx === point.x) || point.x : point.x;
    return [x, point?.lowerSpread, point?.median, point?.upperSpread, point?.sd].map((i) => String(i));
};
export const prepareTabularElementExportedData = (element, simulations, recipes, cellModels, optimizations, processData, processVariables, insights, customVariables, resultRows, calculationRows, incompleteSimulationsIds) => {
    const headers = [
        "Source",
        "Parameter (units)",
        "Parameter type",
        "Point in time",
        "Q0",
        "Q1",
        "Q2",
        "Q3",
        "Q4",
        "Static",
        "Total number of runs",
        "Number of failed runs",
    ];
    const rows = [];
    element?.rows
        ?.filter((row) => isTableElementSourceCompleted(row, incompleteSimulationsIds, optimizations, insights, processVariables, simulations, cellModels, recipes))
        ?.forEach((row) => {
        const { title, subtitle, subSubtitle } = getRowName(row, simulations, processData, [], optimizations);
        const sourceName = [title, subtitle, subSubtitle].filter(Boolean).join("->")?.replaceAll(",", "");
        const rowResult = resultRows?.find(({ rowId }) => rowId === row.id);
        const { status } = (calculationRows?.find(({ responseId }) => row.id === responseId) ||
            {});
        const totalRunsCount = !rowResult?.accuracy
            ? 0
            : getTotalRuns({
                ...rowResult?.accuracy,
                numberOfOptimisationSteps: null,
            }, false);
        if (!element?.columns?.length) {
            const EMPTY_COLUMNS_COUNT = 11;
            rows.push([sourceName, ...Array(EMPTY_COLUMNS_COUNT).fill("")]);
        }
        element?.columns
            ?.filter((column) => !getTableColumnCompletenessMessage(column.parameter, element.rows, simulations, cellModels, recipes, insights, processVariables, incompleteSimulationsIds))
            .forEach((column) => {
            const csvRowData = [];
            let timingCellContent = "";
            let parameterCellContent = "";
            let parameterTypeCellContent = "";
            const { unitsLabel, typeLabel, parameterLabel } = getColumnLabels(column.parameter, processVariables, insights, customVariables, recipes);
            const flowsList = column.parameter.type === ResultElementParameterType.ALTERNATIVE_FLOW
                ? getAlternativeStepFlowsList(recipes, column.parameter.name)
                : undefined;
            const cellResult = rowResult?.columns?.find(({ columnId }) => columnId === column.id) ||
                {};
            const resultCells = renderExportedTabularResultResultCells(rowResult, cellResult, status, row, column, flowsList);
            if (column.parameter.type !== ResultElementParameterType.INSIGHT) {
                const unitsTextContent = unitsLabel ? ` (${getTextContentFromHtml(unitsLabel)})` : "";
                const timestampOptions = getSimulationTimestampOptions(row.sourceId ?? 0, simulations, recipes);
                const timingSummaryText = getTimingSummaryText(column.parameter?.parameter?.cut || null, timestampOptions, processVariables, customVariables);
                parameterCellContent = `${parameterLabel}${unitsTextContent}`;
                parameterTypeCellContent = typeLabel ?? "";
                timingCellContent = getTextContentFromHtml(timingSummaryText)?.replaceAll(",", "") ?? "";
            }
            else {
                parameterCellContent = `${parameterLabel}->${typeLabel?.split(", ").join("->") ?? ""}`;
                parameterTypeCellContent = column.parameter?.type;
            }
            csvRowData.push(sourceName, parameterCellContent, parameterTypeCellContent, timingCellContent, ...resultCells, String(totalRunsCount) ?? "", String(isNotEmpty(cellResult.errorCount) ? cellResult.errorCount : 0));
            rows.push(csvRowData);
        });
    });
    return convertToCSV([headers, ...rows]);
};
const getLineGraphSourceVariableName = (source, simulations, recipes, variableOptionsList) => {
    const processVariableName = variableOptionsList.find((item) => item.value === source?.variableType)
        ?.label ?? "";
    switch (source?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, customVariableId } = { ...source };
            const simulation = simulations?.find(({ id }) => id === simulationId);
            const customVariables = getRecipesCustomVariables(recipes, [simulation?.recipeId]);
            const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
            return customVariableId ? (customVariableEntity?.name ?? "") : processVariableName;
        }
        case ResultElementSourceType.ProcessData: {
            const { liquid } = { ...source };
            return liquid ? liquid : processVariableName;
        }
        default:
            return "";
    }
};
export const getLineGraphSourceUnitOption = (source, simulations, recipes, parameterTypes) => {
    switch (source?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, customVariableId, units } = { ...source };
            const simulation = simulations?.find(({ id }) => id === simulationId);
            const customVariables = getRecipesCustomVariables(recipes, [simulation?.recipeId]);
            const customVariableEntity = customVariables.find((item) => item.id === customVariableId);
            const customVariableUnit = parseComplexUnitToHtml(customVariableEntity?.formula?.configuredUnits?.complexValue)?.html;
            return customVariableId
                ? { value: customVariableUnit, label: customVariableUnit }
                : getRecipeParameterUnitsItems("", units, parameterTypes, []).find((item) => item.value === units);
        }
        case ResultElementSourceType.ProcessData: {
            const { units: processDataSourceUnits } = { ...source };
            return getRecipeParameterUnitsItems("", processDataSourceUnits, parameterTypes, []).find((item) => item.value === processDataSourceUnits);
        }
        default:
            return undefined;
    }
};
export const getResultElementSourceComputedName = (source, simulations, optimizations, insights, recipes, persistedInsightsData, processVariables, processData, customSeparator) => {
    const separator = customSeparator ?? "_";
    switch (source?.type) {
        case ResultElementSourceType.Simulation: {
            const { simulationId, optimizationId, adjustmentId } = { ...source };
            return getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId, separator);
        }
        case ResultElementSourceType.Insight: {
            const { insightId, inputSetId, output } = { ...source };
            return getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes, separator);
        }
        case ResultElementSourceType.ProcessData: {
            const { processDataId } = { ...source };
            const processDataEntity = processData?.find(({ id }) => id === processDataId);
            return processDataEntity?.name;
        }
        default:
            return "";
    }
};
const getParameterAppearanceForExport = (parameter, recipes, cellModels, parameterTypes) => {
    const { type } = { ...parameter.parameter };
    switch (type) {
        case ResultElementParameterType.ALTERNATIVE_FLOW:
            return `${parameter.parameter?.name?.replaceAll(",", ";") ?? ""} () - Recipe parameter`;
        case ResultElementParameterType.RECIPE:
        case ResultElementParameterType.CELL_MODEL: {
            const preparedParameter = getCombinationParameterPreview(parameter, recipes, cellModels, parameterTypes);
            const { name, units } = {
                ...preparedParameter?.parameter,
            };
            const parameterType = type === ResultElementParameterType.CELL_MODEL ? "Cell Model" : "Recipe";
            return `${name?.replaceAll(",", ";") ?? ""} (${getTextContentFromHtml(units)}) - ${parameterType} parameter`;
        }
    }
};
export const prepareLineGraphElementExportedData = (element, simulations, recipes, cellModels, parameterTypes, optimizations, processData, processVariables, insights, persistedInsights, persistedSimulationPreviews, customVariables, lineGraphResult, variableOptionsList, calculationElement, incompleteSimulationsIds) => {
    const headers = [
        "Source",
        "Variable (units)",
        "Timing",
        getParameterAppearanceForExport(element.parameter, recipes, cellModels, parameterTypes),
        "Q1",
        "Q2",
        "Q3",
        "SD",
        "Total number of runs",
        "Number of failed runs",
    ];
    const rows = [];
    const flowsList = element.parameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW
        ? getAlternativeStepFlowsList(recipes, element.parameter.parameter?.name)
        : undefined;
    element?.sources
        ?.filter((source) => isLineGraphSourceCompleted(source, processVariables) &&
        isProcessDataViewerSourceSourceCompleted(source, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes))
        ?.forEach((elementSource) => {
        const sourceCellContent = getResultElementSourceComputedName(elementSource?.source, simulations, optimizations, insights, recipes, persistedInsights, processVariables, processData, "->")?.replace(/,/g, "") ?? "";
        const unitOption = getLineGraphSourceUnitOption(elementSource?.source, simulations, recipes, parameterTypes);
        const units = unitOption?.label ? `(${getTextContentFromHtml(unitOption?.label)})` : "";
        const variableName = getLineGraphSourceVariableName(elementSource?.source, simulations, recipes, variableOptionsList);
        const timestampOptions = getSimulationTimestampOptions(elementSource?.source?.simulationId ?? 0, simulations, recipes);
        const timingSummaryText = getTimingSummaryText(elementSource?.source?.timing || null, timestampOptions, processVariables, customVariables);
        const timingCellContent = getTextContentFromHtml(timingSummaryText)?.replaceAll(",", "") ?? "";
        const sourceResult = lineGraphResult?.find(({ sourceId }) => sourceId === elementSource.id);
        const calculationRow = calculationElement?.rows?.find(({ responseId }) => responseId === elementSource.id) ||
            {};
        const sourceError = getGraphElementSourceError(elementSource, persistedSimulationPreviews, persistedInsights, optimizations, incompleteSimulationsIds, processVariables, insights, simulations, cellModels, recipes);
        const totalRunsCount = !sourceResult?.accuracy
            ? 0
            : getTotalRuns({
                ...sourceResult?.accuracy,
                numberOfOptimisationSteps: null,
            }, false);
        const baseRowData = [
            sourceCellContent,
            `${variableName}${units ? ` ${units}` : ""}`,
            timingCellContent,
        ];
        if ((sourceResult?.points?.length || 0) === 0) {
            rows.push([
                ...baseRowData,
                ...renderExportedProcessDataViewerPointCells(sourceResult, sourceError, calculationRow, undefined, flowsList),
                totalRunsCount ?? "",
                0,
            ]);
        }
        else {
            sourceResult?.points?.forEach((point) => {
                rows.push([
                    ...baseRowData,
                    ...renderExportedProcessDataViewerPointCells(sourceResult, sourceError, calculationRow, point, flowsList),
                    totalRunsCount ?? "",
                    point.errorCount ?? 0,
                ]);
            });
        }
    });
    return convertToCSV([headers, ...rows]);
};
export const prepareProcessDataViewerElementExportedData = (element, simulations, recipes, cellModels, parameterTypes, optimizations, processData, processVariables, insights, persistedInsights, persistedSimulationPreviews, processDataViewerResult, variableOptionsList, calculationElement, incompleteSimulationsIds) => {
    const headers = [
        "Source",
        "Variable (units)",
        "Time (seconds)",
        "Q1",
        "Q2",
        "Q3",
        "SD",
        "Total number of runs",
        "Number of failed runs",
    ];
    const rows = [];
    element?.sources
        ?.filter((source) => isProcessDataViewerSourceCompleted(source) &&
        isProcessDataViewerSourceSourceCompleted(source, insights, optimizations, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes))
        ?.forEach((elementSource) => {
        const sourceCellContent = getResultElementSourceComputedName(elementSource?.source, simulations, optimizations, insights, recipes, persistedInsights, processVariables, processData, "->")?.replace(/,/g, "") ?? "";
        const unitOption = getLineGraphSourceUnitOption(elementSource?.source, simulations, recipes, parameterTypes);
        const units = unitOption?.label ? `(${getTextContentFromHtml(unitOption?.label)})` : "";
        const variableName = getLineGraphSourceVariableName(elementSource?.source, simulations, recipes, variableOptionsList);
        const sourceResult = processDataViewerResult?.find(({ sourceId }) => sourceId === elementSource.id);
        const calculationRow = calculationElement?.rows?.find(({ responseId }) => responseId === elementSource.id) ||
            {};
        const sourceError = getGraphElementSourceError(elementSource, persistedSimulationPreviews, persistedInsights, optimizations, incompleteSimulationsIds, processVariables, insights, simulations, cellModels, recipes);
        const totalRunsCount = !sourceResult?.accuracy
            ? 0
            : getTotalRuns({
                ...sourceResult?.accuracy,
                numberOfOptimisationSteps: null,
            }, elementSource.source?.type === ResultElementSourceType.ProcessData);
        const baseRowData = [sourceCellContent, `${variableName}${units ? ` ${units}` : ""}`];
        if (!sourceResult?.points?.length) {
            rows.push([
                ...baseRowData,
                ...renderExportedProcessDataViewerPointCells(sourceResult, sourceError, calculationRow),
                totalRunsCount ?? "",
                0,
            ]);
        }
        else {
            sourceResult?.points?.forEach((point) => {
                rows.push([
                    ...baseRowData,
                    ...renderExportedProcessDataViewerPointCells(sourceResult, sourceError, calculationRow, point),
                    totalRunsCount ?? "",
                    point.errorCount ?? 0,
                ]);
            });
        }
    });
    return convertToCSV([headers, ...rows]);
};
const prepareTableElementToPrint = (printRef) => {
    const { node, element } = printRef;
    const clonedNode = (node?.cloneNode(true) ?? null);
    const nodeTableRoot = clonedNode.querySelector(`.${tableElementStyles.root}`);
    const nodeTableContent = clonedNode.querySelector(`.${tableElementStyles.content}`);
    const table = clonedNode?.querySelector(`.${tableStyles.table}`);
    if (!table || !nodeTableContent || !nodeTableRoot) {
        return;
    }
    if (element.columns.length > 4) {
        const dividedTables = Array.from(Array(Math.ceil(element.columns.length / 4)), (_, tableIndex) => {
            const clonedTable = table.cloneNode(true);
            const rows = clonedTable.querySelectorAll("tr");
            rows.forEach((row) => {
                const childNodes = [...row.childNodes];
                row.innerHTML = "";
                childNodes.forEach((el, index) => {
                    if (index === 0 || (index > tableIndex * 4 && index <= tableIndex * 4 + 4)) {
                        row.append(el);
                    }
                });
            });
            return clonedTable;
        });
        nodeTableRoot.removeChild(nodeTableContent);
        nodeTableRoot.append(...dividedTables);
        return clonedNode;
    }
    else {
        nodeTableRoot.removeChild(nodeTableContent);
        nodeTableRoot.append(table);
        return clonedNode;
    }
};
const prepareProcessDataViewerLineGraphElementToPrint = async (printRef) => {
    const { element, node } = printRef;
    const styles = element.type === ResultSetElementType.LineGraph ? lineGraphElementStyles : processDataViewerElementStyles;
    const clonedNode = node?.cloneNode();
    const contentContainer = document.createElement("div");
    contentContainer.setAttribute("style", "display: block; width: 100%; page-break-after: always; page-break-inside: avoid");
    clonedNode?.appendChild(contentContainer);
    const elementTopNode = node?.querySelector(`.${styles.top}`);
    const headNode = node?.querySelector(`.${elementItemStyles.head}`)?.cloneNode(true);
    headNode && contentContainer.appendChild(headNode);
    const configNode = node?.querySelector(`.${styles.config}`)?.cloneNode(true);
    if (configNode) {
        configNode
            .querySelectorAll(`.${styles.row}.${styles.faded}`)
            .forEach((el) => el.parentNode?.removeChild(el));
        clonedNode?.appendChild(configNode);
    }
    node?.setAttribute("style", "width: 1350px");
    elementTopNode?.setAttribute("style", "padding-bottom: 0");
    await sleep(200);
    await toSvg(node, {
        filter: (domNode) => {
            if (!domNode.classList) {
                return true;
            }
            else {
                return !(domNode.classList.contains(styles.config) || domNode.classList.contains(elementItemStyles.head));
            }
        },
        height: 650,
    }).then((imageData) => {
        node?.removeAttribute("style");
        elementTopNode?.removeAttribute("style");
        const imageContainer = document.createElement("div");
        imageContainer.setAttribute("style", "display: block; width: 100%;");
        const image = new Image();
        image.src = imageData;
        image.setAttribute("style", "width: 100%; height: auto");
        imageContainer.appendChild(image);
        contentContainer.appendChild(imageContainer);
    });
    return clonedNode;
};
const prepareSpaceViewerElementToPrint = async (printRef) => {
    const { node, element } = printRef;
    const clonedNode = node?.cloneNode();
    const contentContainer = document.createElement("div");
    contentContainer.setAttribute("style", "display: block; width: 100%; page-break-after: auto; page-break-inside: avoid");
    clonedNode?.appendChild(contentContainer);
    const headNode = node?.querySelector(`.${elementItemStyles.head}`)?.cloneNode(true);
    headNode && contentContainer.appendChild(headNode);
    const graphLegendNode = node?.querySelector(`.${spaceViewerStyles.right}`)?.cloneNode(true);
    graphLegendNode && clonedNode?.appendChild(graphLegendNode);
    const configNode = node?.querySelector(`.${spaceViewerElementStyles.config}`)?.cloneNode(true);
    configNode && clonedNode?.appendChild(configNode);
    const graph = node?.querySelector(`.${spaceViewerStyles.left}`);
    graph?.setAttribute("style", "width: 1400px; min-width: 1400px; padding-right: 0;");
    window.dispatchEvent(new Event("resize"));
    await sleep(200);
    await toSvg(graph, element?.parameters?.length === 1 ? { height: 250 } : undefined).then((imageData) => {
        graph?.removeAttribute("style");
        window.dispatchEvent(new Event("resize"));
        const imageContainer = document.createElement("div");
        imageContainer.setAttribute("style", "display: block; width: 100%;");
        const image = new Image();
        image.src = imageData;
        image.setAttribute("style", "width: 100%; height: auto");
        imageContainer.appendChild(image);
        contentContainer?.appendChild(imageContainer);
    });
    return clonedNode;
};
const prepareResultElementToPrint = async (printRef) => {
    if (!printRef.node) {
        return;
    }
    switch (printRef.element.type) {
        case ResultSetElementType.Table:
            return prepareTableElementToPrint(printRef);
        case ResultSetElementType.LineGraph:
        case ResultSetElementType.ProcessDataViewer:
            return await prepareProcessDataViewerLineGraphElementToPrint(printRef);
        case ResultSetElementType.SpaceViewer:
            return await prepareSpaceViewerElementToPrint(printRef);
        default:
            return;
    }
};
export const prepareResultElementsToPrint = async (elementPrintRefs, elementId) => {
    const { current } = elementPrintRefs;
    if (current) {
        if (isNotEmpty(elementId) && current[elementId]) {
            return await prepareResultElementToPrint(current[elementId]);
        }
        else {
            const reducedElements = Object.values(current).reduce((acc, ref) => {
                const row = ref.element.row.toString();
                acc[row] = [...(acc[row] ?? []), ref].sort((a, b) => a.element.column - b.element.column);
                return acc;
            }, {});
            const sortedElements = Object.keys(reducedElements)
                .sort((a, b) => Number(a) - Number(b))
                .reduce((acc, key) => [...acc, ...(reducedElements[key] ?? [])], []);
            return (await Promise.all(sortedElements.map(prepareResultElementToPrint)));
        }
    }
};
